import { useEffect, useState } from "react";
import { toast } from "sonner";
import { SectionHead, FadeUp } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SERVICE_OPTIONS = [
  { v: "corporate", label: "Corporate Wellness" },
  { v: "events", label: "Events & Conference" },
  { v: "touring", label: "Backstage & Touring" },
  { v: "private", label: "Private Session" },
];

const FIELD = "w-full rounded-xl border border-white/15 bg-ink px-4 py-3 text-sm text-ivory placeholder:text-mute/60 focus:border-gold focus:outline-none transition-colors";
const LABEL = "block font-mono text-[9px] uppercase tracking-[0.2em] text-mute mb-2";

export const QuoteSection = () => {
  const [service, setService] = useState("corporate");
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", company: "", city: "", date: "", attendees: "", message: "" });

  useEffect(() => {
    const handler = (e) => { setService(e.detail); setDone(false); };
    window.addEventListener("elite:quote", handler);
    return () => window.removeEventListener("elite:quote", handler);
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Name and email are required.");
      return;
    }
    setSending(true);
    try {
      const res = await fetch(`${API}/quote-requests`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, service_type: service }),
      });
      if (!res.ok) throw new Error(`status ${res.status}`);
      setDone(true);
      toast.success("Request received. Talk soon.");
    } catch (err) {
      toast.error("Something went wrong — please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="quote" className="relative py-24 lg:py-32 bg-ink2/50 border-t border-white/10 overflow-hidden" data-testid="quote-section">
      <div className="spotlight pointer-events-none absolute -bottom-40 left-1/4 h-[38rem] w-[38rem] rounded-full" aria-hidden="true" />
      <div className="relative mx-auto max-w-7xl px-5 sm:px-8 grid lg:grid-cols-2 gap-14 items-start">
        <div>
          <SectionHead
            eyebrow="Request a quote"
            title={<>Let's design your <em className="italic text-gold">wellness day</em>.</>}
            sub="Tell me about your team or event — I'll come back with a tailored plan and pricing. Serving companies, events, productions and private clients across Alberta and Montreal."
          />
          <div className="mt-10 space-y-4">
            {[
              ["Tailored to headcount & space", "From a boardroom of six to a conference floor of six hundred."],
              ["Alberta · Montreal", "Two home bases, one standard of care. Travel for tours and productions by arrangement."],
              ["One practitioner, full stack", "RMT-grade bodywork, fascia work, stretch therapy and nervous-system regulation — no vendor roulette."],
            ].map(([t, d], i) => (
              <FadeUp key={t} delay={i * 0.07}>
                <div className="flex gap-4 rounded-xl border border-white/10 bg-ink/60 p-5" data-testid={`quote-reassurance-${i}`}>
                  <span className="mt-1 h-2 w-2 shrink-0 rotate-45 bg-gold" aria-hidden="true" />
                  <div>
                    <p className="text-ivory font-medium text-sm">{t}</p>
                    <p className="mt-1 text-sm text-mute">{d}</p>
                  </div>
                </div>
              </FadeUp>
            ))}
          </div>
          <FadeUp delay={0.2} className="mt-8">
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-mute" data-testid="quote-contact-line">
              Prefer email?{" "}
              <a href="mailto:akamba81@gmail.com" data-testid="quote-email-link" className="text-gold underline underline-offset-4 hover:text-ivory transition-colors">akamba81@gmail.com</a>
              {" · "}
              <a href="https://instagram.com/elite_therapeutics" target="_blank" rel="noopener noreferrer" data-testid="quote-instagram-link" className="text-gold underline underline-offset-4 hover:text-ivory transition-colors">@elite_therapeutics</a>
            </p>
          </FadeUp>
        </div>

        <FadeUp delay={0.1}>
          <div className="rounded-2xl border border-white/10 bg-panel/80 backdrop-blur p-7 sm:p-9" data-testid="quote-form-card">
            {done ? (
              <div className="py-16 text-center" data-testid="quote-form-success">
                <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full border border-gold/50 bg-gold/10">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path d="M4 12.5l5 5L20 7" stroke="#D4AF37" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
                <h3 className="mt-6 font-serif text-3xl text-ivory font-light">Request received.</h3>
                <p className="mt-3 text-sm text-mute max-w-sm mx-auto">
                  Thank you — your {SERVICE_OPTIONS.find((s) => s.v === service)?.label} inquiry is in.
                  You'll hear back from Ariane shortly.
                </p>
                <button
                  data-testid="quote-form-reset"
                  onClick={() => { setDone(false); setForm({ name: "", email: "", company: "", city: "", date: "", attendees: "", message: "" }); }}
                  className="mt-8 font-mono text-[10px] uppercase tracking-[0.2em] text-gold underline underline-offset-4 hover:text-ivory transition-colors"
                >
                  Send another request
                </button>
              </div>
            ) : (
              <form onSubmit={submit} data-testid="quote-form" noValidate>
                <p className="eyebrow mb-6">The brief</p>
                <div className="grid sm:grid-cols-2 gap-5">
                  <div className="sm:col-span-2">
                    <label className={LABEL} htmlFor="q-service">Service type</label>
                    <select id="q-service" data-testid="quote-form-service" value={service}
                      onChange={(e) => setService(e.target.value)}
                      className={`${FIELD} appearance-none cursor-pointer`} style={{ colorScheme: "dark" }}>
                      {SERVICE_OPTIONS.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className={LABEL} htmlFor="q-name">Your name *</label>
                    <input id="q-name" data-testid="quote-form-name" className={FIELD} value={form.name} onChange={set("name")} placeholder="Full name" required />
                  </div>
                  <div>
                    <label className={LABEL} htmlFor="q-email">Work email *</label>
                    <input id="q-email" type="email" data-testid="quote-form-email" className={FIELD} value={form.email} onChange={set("email")} placeholder="you@company.com" required />
                  </div>
                  <div>
                    <label className={LABEL} htmlFor="q-company">Company / production</label>
                    <input id="q-company" data-testid="quote-form-company" className={FIELD} value={form.company} onChange={set("company")} placeholder="Company or event name" />
                  </div>
                  <div>
                    <label className={LABEL} htmlFor="q-city">City</label>
                    <input id="q-city" data-testid="quote-form-city" className={FIELD} value={form.city} onChange={set("city")} placeholder="Calgary · Edmonton · Montreal…" />
                  </div>
                  <div>
                    <label className={LABEL} htmlFor="q-date">Preferred date</label>
                    <input id="q-date" type="date" data-testid="quote-form-date" className={FIELD} value={form.date} onChange={set("date")} style={{ colorScheme: "dark" }} />
                  </div>
                  <div>
                    <label className={LABEL} htmlFor="q-attendees">Headcount / attendees</label>
                    <input id="q-attendees" data-testid="quote-form-attendees" className={FIELD} value={form.attendees} onChange={set("attendees")} placeholder="e.g. 25 people" />
                  </div>
                  <div className="sm:col-span-2">
                    <label className={LABEL} htmlFor="q-message">Tell me more</label>
                    <textarea id="q-message" data-testid="quote-form-message" rows={4} className={`${FIELD} resize-none`} value={form.message} onChange={set("message")}
                      placeholder="Wellness day? Conference? Tour dates? Anything that helps me prepare…" />
                  </div>
                </div>
                <button
                  type="submit"
                  disabled={sending}
                  data-testid="quote-form-submit"
                  className="mt-7 w-full rounded-full bg-gold py-4 font-mono text-[11px] uppercase tracking-[0.2em] font-medium text-ink transition-all duration-300 hover:bg-ivory hover:shadow-[0_0_44px_rgba(212,175,55,0.35)] disabled:opacity-50 disabled:cursor-wait"
                >
                  {sending ? "Sending…" : service === "corporate" ? "Request a Corporate Wellness Quote" : service === "touring" ? "Request a Backstage & Touring Quote" : "Send request"}
                </button>
                <p className="mt-4 text-center font-mono text-[9px] uppercase tracking-[0.16em] text-mute/70">
                  Private sessions: use the same form — select “Private Session”.
                </p>
              </form>
            )}
          </div>
        </FadeUp>
      </div>
    </section>
  );
};
